import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Eye,
  FileText,
  Package,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { useReturnVoucherQuery } from "@features/return-voucher/hooks/useReturnVoucher";
import ReturnVoucherCard from "./returnVoucherCard";

const ReturnVoucherList = () => {
  const { data, isLoading } = useReturnVoucherQuery();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Filter and sort vouchers
  const filteredAndSortedVouchers = useMemo(() => {
    if (!data) return [];

    let filtered = data.filter((voucher) => {
      const matchesSearch =
        voucher.client?.brandName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        voucher.client?.phone?.includes(searchTerm) ||
        voucher.client?.city
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        voucher._id.includes(searchTerm);

      const matchesStatus =
        statusFilter === "all" || voucher.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case "totalAmount":
          aValue = a.totalAmount || 0;
          bValue = b.totalAmount || 0;
          break;
        case "packageCount":
          aValue = a.packages?.length || 0;
          bValue = b.packages?.length || 0;
          break;
        case "client":
          aValue = a.client?.brandName?.toLowerCase() || "";
          bValue = b.client?.brandName?.toLowerCase() || "";
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [data, searchTerm, statusFilter, sortBy, sortOrder]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!data)
      return {
        total: 0,
        pending: 0,
        paid: 0,
        canceled: 0,
        return_collection: 0,
        uninvoicedPackages: 0,
      };

    const stats = {
      total: data.length,
      pending: data.filter((v) => v.status === "pending").length,
      paid: data.filter((v) => v.status === "paid").length,
      canceled: data.filter((v) => v.status === "canceled").length,
      return_collection: data.filter((v) => v.status === "return_collection")
        .length,
      uninvoicedPackages: 0,
    };

    // Count uninvoiced packages across all vouchers
    data.forEach((voucher) => {
      if (voucher.packages) {
        voucher.packages.forEach((pkg) => {
          if (!pkg.invoiced && !pkg.invoice) {
            stats.uninvoicedPackages++;
          }
        });
      }
    });

    return stats;
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <ThreeDotLoader />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <FileText className="size-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("returnVoucher.noVouchers")}
          </h3>
          <p className="text-gray-600 mb-4">
            {t("returnVoucher.noVouchersDescription")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4 text-center">
            <FileText className="size-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold text-blue-700">
              {stats.total}
            </div>
            <div className="text-sm text-gray-600">
              {t("returnVoucher.totalVouchers")}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4 text-center">
            <Package className="size-6 mx-auto mb-2 text-yellow-600" />
            <div className="text-2xl font-bold text-yellow-700">
              {stats.pending}
            </div>
            <div className="text-sm text-gray-600">{t("status.pending")}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4 text-center">
            <Package className="size-6 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold text-purple-700">
              {stats.return_collection}
            </div>
            <div className="text-sm text-gray-600">
              {t("status.return_collection")}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="size-6 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold text-green-700">
              {stats.paid}
            </div>
            <div className="text-sm text-gray-600">{t("status.paid")}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="size-6 mx-auto mb-2 text-orange-600" />
            <div className="text-2xl font-bold text-orange-700">
              {stats.uninvoicedPackages}
            </div>
            <div className="text-sm text-gray-600">
              {t("invoices.uninvoicedPackages")}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="size-5" />
            {t("common.filtersAndSearch")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400" />
              <Input
                placeholder={t("returnVoucher.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder={t("common.status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.allStatuses")}</SelectItem>
                <SelectItem value="pending">{t("status.pending")}</SelectItem>
                <SelectItem value="return_collection">
                  {t("status.return_collection")}
                </SelectItem>
                <SelectItem value="paid">{t("status.paid")}</SelectItem>
                <SelectItem value="canceled">{t("status.canceled")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder={t("common.sortBy")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">
                  {t("common.dateCreated")}
                </SelectItem>
                <SelectItem value="totalAmount">
                  {t("common.amount")}
                </SelectItem>
                <SelectItem value="packageCount">
                  {t("packages.count")}
                </SelectItem>
                <SelectItem value="client">{t("client.name")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="flex items-center gap-2"
            >
              {sortOrder === "asc" ? (
                <SortAsc className="size-4" />
              ) : (
                <SortDesc className="size-4" />
              )}
              {sortOrder === "asc"
                ? t("common.ascending")
                : t("common.descending")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {t("returnVoucher.vouchers")} ({filteredAndSortedVouchers.length})
        </h3>

        {filteredAndSortedVouchers.length > 0 && (
          <Badge variant="outline" className="text-sm">
            {filteredAndSortedVouchers.length} {t("common.results")}
          </Badge>
        )}
      </div>

      {/* Voucher Cards */}
      {filteredAndSortedVouchers.length > 0 ? (
        <div className="grid lg:grid-cols-2 gap-6">
          {filteredAndSortedVouchers.map((voucher) => (
            <ReturnVoucherCard key={voucher._id} voucher={voucher} />
          ))}
        </div>
      ) : (
        <Card className="text-center py-8">
          <CardContent>
            <Search className="size-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("common.noResults")}
            </h3>
            <p className="text-gray-600">{t("common.tryDifferentFilters")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReturnVoucherList;
